// ============================================
// services/api.ts
//
// 论坛后端 API 调用封装.
//
// 设计要点:
//   - 复用 hipiido-app-api 现有 endpoint (Week 1 阶段 share intent 兑换走它)
//   - 论坛专属 action 走 hipiido-community-api 新 endpoint
//   - 两套 endpoint 通过 ACTION 前缀自动路由
//   - session_token 从 localStorage 读 (key=COMMUNITY_SESSION_TOKEN)
//   - 统一 { ok, error, message } 响应处理
// ============================================

const APP_API_URL = 'https://api.hipiido.cloud/app-api';
const COMMUNITY_API_URL = 'https://api.hipiido.cloud/community-api';

const TOKEN_KEY = 'COMMUNITY_SESSION_TOKEN';

// 哪些 action 走 hipiido-app-api (Studio 共享 + 历史接口)
// 其余默认走 community-api
const APP_API_ACTIONS = new Set([
  'community-share-redeem-intent',  // 这个目前在 share.js 里,属 app-api
  'app-share-config',
  'app-share-create-intent',
  'community-get-categories',  // ⭐ v3.5 (2026-05-20): 分类统一归 app-api 管
  'app-login',  // ⭐ 论坛独立登录 (2026-05-23): 复用 app-api 的用户名+密码登录
]);

export interface ApiResult<T = unknown> {
  ok: boolean;
  error?: string;
  message?: string;
  // 其余字段直接展开
  [k: string]: unknown;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * 调一次后端 action.
 * 自动带 token (除非 opts.noAuth=true).
 */
export async function callApi<T = unknown>(
  action: string,
  body: Record<string, unknown> = {},
  opts: { noAuth?: boolean; signal?: AbortSignal } = {},
): Promise<ApiResult<T>> {
  const url = APP_API_ACTIONS.has(action) ? APP_API_URL : COMMUNITY_API_URL;

  const payload: Record<string, unknown> = { action, ...body };
  if (!opts.noAuth) {
    const t = getToken();
    if (t) payload.token = t;
  }

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: opts.signal,
    });
  } catch (e) {
    return {
      ok: false,
      error: 'NETWORK_ERROR',
      message: '网络异常,请检查网络后重试',
    };
  }

  if (!resp.ok) {
    return {
      ok: false,
      error: 'HTTP_' + resp.status,
      message: `请求失败 (${resp.status})`,
    };
  }

  try {
    const data = await resp.json();
    // ⚠️ 关键兼容: hipiido-app-api 用 `success` 字段, hipiido-community-api 用 `ok`.
    //   归一化成 `ok`, 让前端统一只判断 r.ok.
    //   (app-share-config / create-intent / redeem-intent 走 app-api, 返 success;
    //    community-* 走 community-api, 返 ok)
    if (data && typeof data === 'object') {
      if (data.ok === undefined && data.success !== undefined) {
        data.ok = data.success;
      }
    }
    return data as ApiResult<T>;
  } catch {
    return {
      ok: false,
      error: 'BAD_JSON',
      message: '服务器响应格式错误',
    };
  }
}
